import {ApiClient} from '@hconnect/apiclient'
import {useUpmPlantTimezone} from '@hconnect/upm'
import {useQuery, UseQueryOptions} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {useEffect} from 'react'

import {AggregatedStoppage, AggregatedStoppagesPayload} from '../types'

const QueryKey = 'aggregated-stoppages'

type Options<TSelect> = Omit<
  UseQueryOptions<AggregatedStoppage[], AxiosError, TSelect>,
  'queryKey' | 'queryFn'
> & {
  onError?: (error: AxiosError) => void
}

export const useAggregatedStoppages = <TSelect = AggregatedStoppage[]>(
  plantId: string,
  payload: AggregatedStoppagesPayload,
  options?: Options<TSelect>
) => {
  const timezone = useUpmPlantTimezone(plantId)

  const query = useQuery<AggregatedStoppage[], AxiosError, TSelect>({
    queryKey: [QueryKey, plantId, payload, timezone],
    queryFn: async ({signal}) => {
      const response = await ApiClient.axiosInstance.post<AggregatedStoppage[]>(
        `/downtime-dashboard/${plantId}/aggregated-stoppages`,
        {
          ...payload
        },
        {signal}
      )

      return response.data
    },
    staleTime: 1000 * 60 * 15,
    ...options
  })

  const onError = options?.onError
  useEffect(() => {
    if (query.error) {
      onError?.(query.error)
    }
  }, [query.error, onError])

  return query
}
